import { Component } from '@theme/component';

class ShowMorePillsComponent extends Component {
  requiredRefs = ['showMoreButton', 'showMoreContent'];

  #resizeObserver;
  #refreshObserver;
  #refreshForm;
  #boundContent;
  #visibilityUpdateQueued = false;
  #expanded = false;
  #pillsPointerStartX = 0;
  #pillsScrollStartX = 0;
  #isDragging = false;
  #hasDragged = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.showMoreButton?.setAttribute('aria-expanded', 'false');
    this.#bindContent();
    this.#setupRefreshObserver();
    this.#updateButtonVisibility();
    requestAnimationFrame(() => this.#updateButtonVisibility());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindContent();
    this.#setupRefreshObserver();
    this.#queueVisibilityUpdate();
  }

  disconnectedCallback() {
    this.#unbindContent();
    this.#refreshObserver?.disconnect();
    this.#refreshObserver = undefined;
    this.#refreshForm = undefined;
    super.disconnectedCallback?.();
  }

  #bindContent = () => {
    const { showMoreContent } = this.refs;
    if (!showMoreContent || showMoreContent === this.#boundContent) return;

    this.#unbindContent();
    this.#boundContent = showMoreContent;
    this.#setupOverflowObserver();
    this.#setupInteractions();
  };

  #unbindContent = () => {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#boundContent?.removeEventListener('wheel', this.#onWheel);
    this.#boundContent?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#boundContent?.removeEventListener('pointermove', this.#onPointerMove);
    this.#boundContent?.removeEventListener('pointerup', this.#onPointerUp);
    this.#boundContent?.removeEventListener('pointercancel', this.#onPointerUp);
    this.#boundContent?.removeEventListener('click', this.#onClickCapture, true);
    this.#boundContent = undefined;
  };

  #setupRefreshObserver = () => {
    const section = this.closest('.shopify-section');
    const form = this.closest('facets-form-component')?.querySelector('form');

    if (!section || !form) {
      this.#refreshObserver?.disconnect();
      this.#refreshObserver = undefined;
      this.#refreshForm = undefined;
      return;
    }

    if (form === this.#refreshForm) return;

    this.#refreshObserver?.disconnect();
    this.#refreshForm = form;
    this.#refreshObserver = new MutationObserver((mutations) => {
      if (mutations.some(({ target }) => target === this.#refreshForm)) {
        this.#queueVisibilityUpdate();
      }
    });
    this.#refreshObserver.observe(section, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-refresh'],
    });
  };

  #setupOverflowObserver = () => {
    const { showMoreContent } = this.refs;
    if (!showMoreContent) return;

    this.#resizeObserver = new ResizeObserver(() => this.#updateButtonVisibility());
    this.#resizeObserver.observe(showMoreContent);
    const pillsList = showMoreContent.querySelector('.facets__inputs-list--pills');
    if (pillsList) this.#resizeObserver.observe(pillsList);

  };

  #queueVisibilityUpdate = () => {
    if (this.#visibilityUpdateQueued) return;

    this.#visibilityUpdateQueued = true;
    queueMicrotask(() => {
      this.#visibilityUpdateQueued = false;
      this.#updateButtonVisibility();
    });
  };

  #setupInteractions = () => {
    const { showMoreContent } = this.refs;
    if (!showMoreContent) return;

    showMoreContent.addEventListener('wheel', this.#onWheel, { passive: false });
    showMoreContent.addEventListener('pointerdown', this.#onPointerDown);
    showMoreContent.addEventListener('pointermove', this.#onPointerMove);
    showMoreContent.addEventListener('pointerup', this.#onPointerUp);
    showMoreContent.addEventListener('pointercancel', this.#onPointerUp);
    showMoreContent.addEventListener('click', this.#onClickCapture, true);
  };

  #getContentWidth = () => {
    const { showMoreContent } = this.refs;
    const pillsList = showMoreContent?.querySelector('.facets__inputs-list--pills');
    if (!showMoreContent || !pillsList) return showMoreContent?.scrollWidth ?? 0;

    const items = pillsList.querySelectorAll('.facets__inputs-list-item');
    if (!items.length) return pillsList.scrollWidth;
    const listRect = pillsList.getBoundingClientRect();
    const firstRect = items[0].getBoundingClientRect();
    const lastRect = items[items.length - 1].getBoundingClientRect();
    return Math.max(0, lastRect.right - firstRect.left);
  };

  #canScroll = () => {
    const { showMoreContent } = this.refs;
    return Boolean(showMoreContent && this.#getContentWidth() > showMoreContent.clientWidth + 1);
  };

  #isInteractiveTarget = (target) =>
    target instanceof Element && Boolean(target.closest('a, button, input, label, select, textarea, [role="button"], [role="link"]'));

  #onWheel = (event) => {
    if (this.#expanded || event.ctrlKey || !this.#canScroll()) return;
    const { showMoreContent } = this.refs;
    if (!showMoreContent) return;
    const modeFactor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? showMoreContent.clientWidth : 1;
    const deltaX = event.deltaX * modeFactor;
    const deltaY = event.deltaY * modeFactor;
    const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
    if (!delta) return;
    const previousScrollLeft = showMoreContent.scrollLeft;
    showMoreContent.scrollLeft += delta;
    if (showMoreContent.scrollLeft !== previousScrollLeft) event.preventDefault();
  };

  #onPointerDown = (event) => {
    if (this.#expanded || !this.#canScroll() || (event.pointerType === 'mouse' && event.button !== 0)) return;
    if (this.#isInteractiveTarget(event.target)) return;
    const { showMoreContent } = this.refs;
    this.#isDragging = true;
    this.#hasDragged = false;
    this.#pillsPointerStartX = event.clientX;
    this.#pillsScrollStartX = showMoreContent.scrollLeft;
    showMoreContent.classList.add('is-dragging');
    showMoreContent.setPointerCapture?.(event.pointerId);
  };

  #onPointerMove = (event) => {
    if (!this.#isDragging || this.#expanded) return;
    const { showMoreContent } = this.refs;
    const deltaX = event.clientX - this.#pillsPointerStartX;
    if (Math.abs(deltaX) > 3) this.#hasDragged = true;
    showMoreContent.scrollLeft = this.#pillsScrollStartX - deltaX;
    event.preventDefault();
  };

  #onPointerUp = (event) => {
    if (!this.#isDragging) return;
    const { showMoreContent } = this.refs;
    this.#isDragging = false;
    showMoreContent?.classList.remove('is-dragging');
    showMoreContent?.releasePointerCapture?.(event.pointerId);
  };

  #onClickCapture = (event) => {
    if (!this.#hasDragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.#hasDragged = false;
  };

  #updateButtonVisibility = () => {
    const { showMoreButton, showMoreContent } = this.refs;
    if (!showMoreButton || !showMoreContent) return;
    const wasExpanded = this.#expanded;
    if (wasExpanded) {
      this.dataset.expanded = 'false';
      showMoreContent.offsetWidth;
    }
    const hasOverflow = this.#canScroll();
    if (!hasOverflow) {
      this.#expanded = false;
      this.dataset.expanded = 'false';
      showMoreButton.setAttribute('aria-expanded', 'false');
    } else if (wasExpanded) {
      this.dataset.expanded = 'true';
    }
    showMoreButton.hidden = !hasOverflow;
  };

  toggle = (event) => {
    event.preventDefault();
    this.#expanded = !this.#expanded;
    this.dataset.expanded = this.#expanded ? 'true' : 'false';
    this.refs.showMoreButton.setAttribute('aria-expanded', this.dataset.expanded);
    this.#updateButtonVisibility();
  };
}

if (!customElements.get('show-more-pills-component')) {
  customElements.define('show-more-pills-component', ShowMorePillsComponent);
}